/**
 * AI API service for content generation and model management.
 * @module lib/api/ai
 */

import type { AIModel } from '@scrambl/shared'

import { apiClient } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface AIGenerateRequest {
  action: string
  prompt: string
  context?: Record<string, unknown>
  systemPromptKey?: string
}

export interface AIGeneratedTask {
  name?: string
  description?: string
  priority?: string
  estimatedHours?: number
  taskTypeDefId?: string
}

export interface AIGeneratedTaskGroup {
  existingGroupName?: string
  name?: string
  description?: string
  color?: string
  tasks?: AIGeneratedTask[]
}

export interface AIGeneratedProjectTasks {
  groups: AIGeneratedTaskGroup[]
  ungroupedTasks: AIGeneratedTask[]
}

export interface AIGeneratedSubtasks {
  subtasks: AIGeneratedTask[]
}

/** Record a description can be generated for. Mirrors the API's union exactly. */
export type AIDescriptionItemType = 'task' | 'entity' | 'project' | 'user_story'

export interface AIGenerateDescriptionRequest {
  itemName: string
  itemType: AIDescriptionItemType
  additionalInfo?: string
  projectId?: string
  entityId?: string
}

export interface AIRewriteDescriptionRequest {
  currentDescription: string
  instructions?: string
  /** Live title of the record — sent so unsaved edits still reach Benny. */
  itemName?: string
  /** Fallback surroundings for records that aren't saved yet. */
  projectId?: string
  entityId?: string
  context?: {
    type: 'task' | 'entity' | 'project'
    id: string
  }
}

export interface AIGenerateSubtasksRequest {
  parentTaskId: string
  prompt: string
}

export interface AIGenerateCommentRequest {
  prompt: string
  taskId?: string
  entityId?: string
}

export interface AIGenerateTasksRequest {
  prompt: string
  projectId?: string
  entityId?: string
  parentTaskId?: string
}

export interface AIGenerateUserStoryRequest {
  prompt: string
  projectId?: string
  entityId?: string
  featureName?: string
}

export interface AIGenerateResponse {
  content: string
  usage?: {
    promptTokens: number
    completionTokens: number
    totalTokens: number
  }
  /**
   * Backend-parsed, normalized structure for task/subtask generation
   * (`generate-project-tasks`, `generate-subtasks`). Present only for those
   * actions; consumers should prefer this over parsing `content` themselves.
   */
  parsed?: AIGeneratedProjectTasks | AIGeneratedSubtasks
}

export interface AIModelWithProvider extends AIModel {
  provider: {
    id: string
    name: string
    type: string
  }
}

export interface AIUsage {
  id: string
  feature: string
  promptTokens: number
  responseTokens: number
  totalTokens: number
  creditsUsed: number
  status: string
  createdAt: string
  user: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
  model: AIModelWithProvider
}

export interface AIUsageFilters {
  startDate?: string
  endDate?: string
  feature?: string
  userId?: string
}

export interface AIUsageTotals {
  count: number
  promptTokens: number
  responseTokens: number
  totalTokens: number
  creditsUsed: number
  successfulCount: number
}

export interface AIUsageResponse {
  usage: AIUsage[]
  totals: AIUsageTotals
}

export interface AIUsagePaginatedParams extends AIUsageFilters {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface AIUsagePaginatedResponse {
  data: AIUsage[]
  meta: {
    currentPage: number
    itemsPerPage: number
    totalItems: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
  totals: AIUsageTotals
}

export interface AICreditStatus {
  configured: boolean
  remainingCredits: number
  monthlyCredits: number | null
  creditsUsedThisPeriod: number | null
  periodEnd: string | null
  requiredCredits: number
  canUse: boolean
  feature?: string
}

class AIApiService {
  async getCreditStatus(feature?: string): Promise<AICreditStatus> {
    const query = feature ? `?feature=${encodeURIComponent(feature)}` : ''
    const response = await apiClient<{ data: AICreditStatus }>(
      `/ai/credits${query}`,
    )
    return response.data
  }

  /**
   * Runs an AI field against one record and stores the result.
   *
   * Explicit rather than automatic: creating the field is free, running it
   * spends AI credits.
   */
  async resolveField(
    fieldId: string,
    moduleType: string,
    moduleId: string,
  ): Promise<{ value: unknown | null }> {
    return apiClient<{ value: unknown | null }>(
      `/ai/fields/${fieldId}/resolve`,
      {
        method: 'POST',
        body: { moduleType, moduleId },
      },
    )
  }

  /**
   * Generates content using AI
   * @param request - Generation request parameters
   * @returns Generated content and usage stats
   */
  async generate(request: AIGenerateRequest): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/generate',
      {
        method: 'POST',
        body: request,
      },
    )
    return response.data
  }

  /**
   * Draft a description for a record from its name.
   *
   * Distinct from `generate({ action: 'generate-description' })`: this hits the
   * purpose-built endpoint, which loads the named project/entity as context and
   * bills the `description_generation` feature. Costs credits.
   */
  async generateDescription(
    request: AIGenerateDescriptionRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/descriptions/generate',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Rewrite an existing description, optionally following an instruction
   * ("make it shorter", "add acceptance criteria"). Costs credits.
   */
  async rewriteDescription(
    request: AIRewriteDescriptionRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/descriptions/rewrite',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Break a task into subtasks.
   *
   * The server resolves `parentTaskId` into prompt context itself, so the
   * caller's `prompt` only needs to say what kind of breakdown is wanted.
   * Returns `parsed.subtasks` alongside the raw content. Costs credits.
   */
  async generateSubtasks(
    request: AIGenerateSubtasksRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/subtasks/generate',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Draft a comment for a task or entity. Costs credits.
   *
   * Returns a draft only — nothing is posted. The caller is responsible for
   * putting it in front of the user before it becomes a real comment.
   */
  async generateComment(
    request: AIGenerateCommentRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/comments',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Generate a task list from a free-text brief, scoped to a project, entity
   * or parent task. Costs credits.
   */
  async generateTasks(
    request: AIGenerateTasksRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/tasks',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Generate a user story. Costs credits.
   */
  async generateUserStory(
    request: AIGenerateUserStoryRequest,
  ): Promise<AIGenerateResponse> {
    const response = await apiClient<{ data: AIGenerateResponse }>(
      '/ai/user-stories',
      { method: 'POST', body: request },
    )
    return response.data
  }

  /**
   * Generates tasks for a project
   * @param projectName - Name of the project
   * @param projectDescription - Description of the project
   * @param additionalContext - Optional additional context
   * @returns Array of generated task descriptions
   */
  async generateProjectTasks(
    projectName: string,
    projectDescription: string,
    additionalContext?: string,
  ): Promise<string[]> {
    const prompt = `Generate a list of tasks for the following project:

Project Name: ${projectName}
Project Description: ${projectDescription}
${additionalContext ? `Additional Context: ${additionalContext}` : ''}

Please provide a list of actionable tasks that would help complete this project. Each task should be clear, specific, and achievable.`

    const response = await this.generate({
      action: 'generate-project-tasks',
      prompt,
      context: { projectName, projectDescription, additionalContext },
    })

    return response.content
      .split('\n')
      .map(task => task.trim())
      .filter(task => task.length > 0 && !task.startsWith('#'))
  }

  /**
   * Gets available AI models
   * @returns Array of available models with provider info
   */
  async getModels(): Promise<AIModelWithProvider[]> {
    const response = await apiClient<{ data: AIModelWithProvider[] }>(
      '/ai/models',
    )
    return response.data
  }

  /**
   * Gets organization's model preference
   * @returns Current model preference or null
   */
  async getModelPreference(): Promise<{ model: AIModelWithProvider } | null> {
    const response = await apiClient<{
      data: { model: AIModelWithProvider } | null
    }>('/ai/models/preference')
    return response.data
  }

  /**
   * Gets AI usage statistics
   * @param filters - Optional filters
   * @returns Usage data and totals
   */
  async getUsage(filters?: AIUsageFilters): Promise<AIUsageResponse> {
    const queryString = filters ? buildQueryString(filters) : ''
    const response = await apiClient<{ data: AIUsageResponse }>(
      `/ai/usage${queryString}`,
    )
    return response.data
  }

  /**
   * Gets paginated AI usage statistics
   * @param params - Pagination and filter parameters
   * @returns Paginated usage data with totals
   */
  async getUsagePaginated(
    params: AIUsagePaginatedParams = {},
  ): Promise<AIUsagePaginatedResponse> {
    const queryString = buildQueryString(params)
    return apiClient<AIUsagePaginatedResponse>(`/ai/usage${queryString}`)
  }
}

export const aiApi = createSingletonApi(AIApiService)
